import type { ComputedRef, MaybeRefOrGetter } from 'vue';
import type { ResolvedMonospaceSingleRelationField } from '#layers/data-model/public';
import type { FormBlockContext } from '#layers/forms/types/form-block';
import { computed, toValue } from 'vue';
import { getRelationConstraint, getRelationConstraintFields } from '#layers/data-model/public';

export interface UseRelationFieldChangesReturn {
	hasChanges: ComputedRef<boolean>;
	/** Restores the relation payload and any foreign key the form was holding a pending value for. */
	revert: () => void;
}

export interface UseRelationFieldChangesOptions {
	formBlockContext: FormBlockContext | null;
	relationField: MaybeRefOrGetter<ResolvedMonospaceSingleRelationField>;
	name: MaybeRefOrGetter<string | undefined>;
}

/**
 * A to-one connect or disconnect writes the relation payload *and* the foreign key primitives that
 * back it, and the input reads the latter. Tracking or reverting the payload alone leaves the field
 * showing the pending value while the form stays dirty.
 */
export function useRelationFieldChanges(options: UseRelationFieldChangesOptions): UseRelationFieldChangesReturn {
	const { formBlockContext, relationField, name } = options;

	// A to-many's foreign key sits on the related rows, so this side holds nothing to restore.
	const foreignKeyNames = computed(() => {
		const { constraint, thisSideConstrained } = getRelationConstraint(toValue(relationField));
		if (!constraint || !thisSideConstrained) return [];

		return getRelationConstraintFields(constraint).constrainedFields.map((field) => field.apiName);
	});

	const changedNames = computed(() => {
		const fieldName = toValue(name);
		const changedProps = formBlockContext?.state.value?.$changedProps;
		if (!fieldName || !changedProps) return [];

		return [fieldName, ...foreignKeyNames.value].filter((candidate) => !!changedProps[candidate]);
	});

	function revert() {
		const state = formBlockContext?.state.value;
		if (!state) return;

		for (const changedName of changedNames.value) {
			state[changedName] = state.$changedProps?.[changedName]?.[1];
		}
	}

	return {
		hasChanges: computed(() => changedNames.value.length > 0),
		revert,
	};
}
